import React, { useEffect, useReducer, useMemo, useCallback } from 'react'
import BasicsService from './Service'
import basicsReducer from './store/Reducer'
import { pick, cloneDeep } from 'lodash'
import { setResponses } from './store/Actions'
import { basicsInitialState } from './store/State'
import { IResponseUpdateRequestPayload, IQuestions, IResponsesValue } from 'types'
import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'

import SessionManagerService from 'smart-components/session-manager/Service'
import { useServiceState, useNotification } from 'helpers'
import { IProps } from './Interface'

const basicsContext = {
  state: {
    ...basicsInitialState,
    questions: {}
  },
  handlers: {
    handleUpdateResponse: (_payload: IResponseUpdateRequestPayload) => {},
    handleChangeResponse: (_payload: IResponseUpdateRequestPayload) => {},
    handleChangeSchool: (_payload: any) => {},
    handleUpdateSchool: (_index: number) => {},
    handleAddNewSchool: (_index: number, _name: string) => {},
    handleSearchSchool: (_name: string) => {},
    handleAddSchool: (_event: React.MouseEvent<HTMLElement>) => {},
    handleRemoveSchool: (_event: React.MouseEvent<SVGSVGElement, MouseEvent>, _index: number) => {}
  }
}

export type BasicsContext = typeof basicsContext & {
  state: { questions: IQuestions }
}

export const BasicsContextProvider = React.createContext<BasicsContext>(basicsContext)

const Provider: React.FC<IProps> = ({ children, responses, user }) => {
  const [state, dispatch] = useReducer(basicsReducer, basicsInitialState)
  const sessionState = useServiceState(SessionManagerService, false)
  const { emitError } = useNotification()

  const userResponsesManagerState = useServiceState(UserResponsesManagerService, false)

  const currentUser = useMemo(() => user || sessionState.currentUser || {}, [user, sessionState])

  const currentResponses = useMemo(
    () => responses || userResponsesManagerState.userResponses?.responses || ({} as IResponsesValue),
    [responses, userResponsesManagerState.userResponses]
  )
  const questions: IQuestions = useMemo(
    () => userResponsesManagerState.userResponses?.questions || ({} as IQuestions),
    [userResponsesManagerState.userResponses]
  )

  const updateResponse = useCallback(
    async (payload: IResponseUpdateRequestPayload) => {
      if (responses) {
        return
      }
      const error = await UserResponsesManagerService.updateResponse(payload)
      if (error) {
        emitError(error)
      }
    },
    [responses, emitError]
  )

  const basicsResponses = useMemo(() => {
    return {
      ...pick(cloneDeep(currentUser), ['firstName', 'lastName', 'avatar']),
      ...pick(cloneDeep(currentResponses), ['schools', 'currentRole', 'currentCompany', 'hometownLocation'])
    } as any
  }, [currentUser, currentResponses])

  useEffect(() => {
    const length = basicsResponses?.schools?.length
    if (length === 0) {
      basicsResponses.schools.push({
        schoolId: '',
        schoolName: '',
        schoolType: '',
        gradYear: null,
        degreeType: '',
        fieldOfStudy: ''
      })
    }

    dispatch(setResponses(basicsResponses))
  }, [basicsResponses])

  const handlers = {
    handleUpdateResponse: BasicsService.handleUpdateResponse(basicsResponses, updateResponse),
    handleChangeResponse: BasicsService.handleChangeResponse(dispatch),
    handleChangeSchool: BasicsService.handleChangeSchool(dispatch),
    handleUpdateSchool: BasicsService.handleUpdateSchool(state, responses, updateResponse),

    handleSearchSchool: BasicsService.handleSearchSchool(dispatch, () => {}),
    handleAddNewSchool: BasicsService.handleAddNewSchool(dispatch, emitError),
    handleAddSchool: (event: React.MouseEvent<HTMLElement>) => {
      const handler = BasicsService.handleAddSchool(dispatch)
      handler()
      event.stopPropagation()
    },
    handleRemoveSchool: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, index: number) => {
      const handler = BasicsService.handleRemoveSchool(dispatch, state, updateResponse)
      handler(index)
      event.stopPropagation()
    }
  }

  const contextValue = {
    state: {
      ...state,
      questions
    },
    handlers
  }

  return <BasicsContextProvider.Provider value={contextValue}>{children}</BasicsContextProvider.Provider>
}

export default Provider
